import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import api from "../../services/api";

export default function useSchedules({ companyId, socket, startDate, endDate, status, searchParam }) {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [revision, setRevision] = useState(0);
  const sequence = useRef(0), inFlight = useRef(false), pending = useRef(false), lastQuery = useRef(null);
  const refresh = useCallback(() => {
    if (inFlight.current) pending.current = true;
    else setRevision(value => value + 1);
  }, []);

  useEffect(() => {
    const request = ++sequence.current;
    const source = axios.CancelToken.source();
    const queryKey = JSON.stringify([companyId, startDate, endDate, status, searchParam]);
    if (lastQuery.current !== queryKey) { setSchedules([]); lastQuery.current = queryKey; }
    inFlight.current = true; pending.current = false;
    setLoading(true); setError(false);
    (async () => {
      // Fetch all pages in the visible period, never the entire schedule history.
      const rows = new Map();
      for (let pageNumber = 1; ; pageNumber += 1) {
        const { data } = await api.get("/schedules", {
          params: { startDate, endDate, status: status || undefined, searchParam, pageNumber, pageSize: 100 },
          timeout: 20000, cancelToken: source.token
        });
        if (request !== sequence.current) return;
        const before = rows.size;
        data.schedules.forEach(row => rows.set(row.id, row));
        if (!data.hasMore) break;
        if (rows.size === before) throw new Error("Paginação sem progresso");
      }
      if (request === sequence.current) setSchedules(Array.from(rows.values()));
    })().catch(err => {
      if (request === sequence.current && !axios.isCancel(err)) setError(true);
    }).finally(() => {
      if (request !== sequence.current) return;
      inFlight.current = false; setLoading(false);
      if (pending.current) { pending.current = false; setRevision(value => value + 1); }
    });
    return () => { sequence.current += 1; inFlight.current = false; source.cancel(); };
  }, [companyId, startDate, endDate, status, searchParam, revision]);

  useEffect(() => {
    if (!socket || !companyId) return undefined;
    let timer;
    const changed = data => {
      if (["create", "update", "delete"].includes(data.action) && !timer) {
        timer = setTimeout(() => { timer = null; refresh(); }, 500);
      }
    };
    const event = `company${companyId}-schedule`;
    socket.on(event, changed);
    return () => { clearTimeout(timer); socket.off(event, changed); };
  }, [socket, companyId, refresh]);
  return { schedules, loading, error, refresh };
}
